/**
 * The grooming run queue (grooming contract 3, epic PI-SPEC-SW-E284).
 *
 * SpecTree Toolbox keeps one queue per computer, saved to disk: every start
 * button (Groom this item, Run batch, Draft product areas, Draft author
 * questions) adds an entry, and the app runs up to `concurrency` entries at
 * once on the person's own Claude Code seat. Formulator turns do not use this
 * queue.
 *
 * This module is the one definition of the queue's shapes, read by the app
 * and by the SpecTree pages: entries, the queue state the app reports on
 * `grooming.getQueue()` and in `queue-changed` events, pause reasons, Claude
 * usage snapshots, the concurrency limits and the plain setup fixes. Every
 * sentence a person sees about the queue lives in `queue-copy.ts`.
 *
 * Constraint inventory: {@link GROOMING_QUEUE_CONCURRENCY} is the only place
 * the 1-4 range and the default of 2 live. No other layer may hard-code them.
 *
 * Request schemas are strict; response schemas (what the app reports) are
 * lenient, so a newer app may add fields without a contract bump. Depends on
 * zod only.
 */
import { z } from "zod";
import { GROOMING_ACTIVITY_MAX_CHARS } from "./constants.js";
import { GROOMING_ENGINE_ERROR_CODES } from "./errors.js";
import { GROOMING_QUEUE_SETUP_CODES, GROOMING_QUEUE_SETUP_COPY } from "./queue-copy.js";
import { GROOMING_BATCH_MODES, GROOMING_RUN_PHASES } from "./types.js";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/**
 * How many queue entries run at once. Adjustable per person between `min`
 * and `max`; `default` until they change it.
 */
export const GROOMING_QUEUE_CONCURRENCY = { min: 1, max: 4, default: 2 };
/**
 * Fraction of a Claude usage window (5-hour or 7-day) the queue does not
 * cross: it does not start an entry whose estimated share would bring the
 * window's utilization to this value or above, and pauses until the window
 * resets instead.
 */
export const USAGE_PAUSE_THRESHOLD = 0.9;
/** Most finished entries the queue state keeps in `completed` (newest first). */
export const GROOMING_QUEUE_COMPLETED_MAX = 50;
/** Longest plain message on a queue entry. */
export const GROOMING_QUEUE_MESSAGE_MAX_CHARS = 500;
/** Longest plain setup fix on a paused queue. */
export const GROOMING_QUEUE_FIX_MAX_CHARS = 300;
/**
 * How often a queue paused for `NETWORK` checks the connection again by
 * itself (frame B3: "It checks again every minute"). Fix it checks at once.
 */
export const GROOMING_QUEUE_NETWORK_RECHECK_MS = 60_000;
// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------
/**
 * What a queue entry runs: brief one tracker item (`groom-item`, also each
 * item of a batch), draft a project's product areas (`product-areas`) or
 * draft questions for an item's author (`author-questions`).
 */
export const GROOMING_QUEUE_ENTRY_KINDS = ["groom-item", "product-areas", "author-questions"];
/**
 * Where a queue entry stands:
 * - `queued`: waiting for a free run slot;
 * - `running`: Claude Code is working on it;
 * - `briefed`: a `groom-item` run whose brief SpecTree confirmed;
 * - `not-ready`: a `groom-item` run that found the item below the grooming bar;
 * - `done`: a `product-areas` or `author-questions` run that finished;
 * - `failed`: the run stopped on a failure (see `errorCode` and `message`);
 * - `stopped`: the person stopped it.
 */
export const GROOMING_QUEUE_ENTRY_STATES = [
    "queued",
    "running",
    "briefed",
    "not-ready",
    "done",
    "failed",
    "stopped",
];
/** The entry states that mean the entry is finished (it belongs in `completed`). */
export const GROOMING_QUEUE_FINISHED_STATES = ["briefed", "not-ready", "done", "failed", "stopped"];
/**
 * Why the queue is paused:
 * - `launch`: the app just started with entries waiting ("3 items waiting · Resume"), or the person signed out of SpecTree (since PKG-PM-B, epic PI-SPEC-SW-E290); it never restarts on its own;
 * - `sleep`: the computer slept; running entries went back to queued (since PKG-PM-B the app starts again by itself on wake);
 * - `setup`: a failure every remaining entry would hit the same way (see `errorCode` and `fix`); since PKG-PM-B a successful Fix it starts the queue again by itself;
 * - `usage-limit`: the next entry would reach {@link USAGE_PAUSE_THRESHOLD}; resumes on its own at `resumeAt`;
 * - `user`: the person pressed Stop all.
 */
export const GROOMING_QUEUE_PAUSE_REASONS = ["launch", "sleep", "setup", "usage-limit", "user"];
/**
 * Why a paused queue started again by itself (epic PI-SPEC-SW-E290,
 * 01-roles.md section 9.6): the computer woke, a Fix it worked, or the
 * connection came back after a `NETWORK` pause.
 */
export const GROOMING_QUEUE_PICKED_UP_AFTER = ["wake", "fix", "connection"];
/** The queue as a whole: nothing to do, working, or paused (see `pause`). */
export const GROOMING_QUEUE_STATUSES = ["idle", "running", "paused"];
// ---------------------------------------------------------------------------
// Entry key
// ---------------------------------------------------------------------------
/**
 * The de-duplication key of a queue entry: one entry per key may be queued or
 * running, so an item cannot be added twice. Project names are compared
 * case-insensitively (ADO project names are), so the project is trimmed and
 * lower-cased:
 * - `groom-item`: `groom:<project>:<itemId>`;
 * - `product-areas`: `areas:<project>`;
 * - `author-questions`: `questions:<project>:<itemId>`.
 *
 * Throws when `groom-item` or `author-questions` gets no item id.
 */
export function queueEntryKey(kind, project, itemId) {
    const projectKey = project.trim().toLowerCase();
    if (kind === "product-areas")
        return `areas:${projectKey}`;
    const item = itemId?.trim() ?? "";
    if (item.length === 0)
        throw new Error(`queueEntryKey: ${kind} needs an item id`);
    return kind === "groom-item" ? `groom:${projectKey}:${item}` : `questions:${projectKey}:${item}`;
}
// ---------------------------------------------------------------------------
// Claude usage
// ---------------------------------------------------------------------------
/**
 * One Claude usage window as the last `rate_limit_event` reported it:
 * `utilization` from 0 to 1, and when the window resets (ISO 8601), or null
 * when Claude did not say.
 */
export const claudeUsageWindowSchema = z.object({
    utilization: z.number().min(0).max(1),
    resetsAt: z.string().nullable(),
});
/**
 * The person's Claude usage, last known. The numbers arrive only once a run
 * talks to Claude, so before the first run the app reports the last value it
 * saw (`observedAt`, ISO 8601). A window Claude did not report is null.
 */
export const claudeUsageSnapshotSchema = z.object({
    fiveHour: claudeUsageWindowSchema.nullable(),
    sevenDay: claudeUsageWindowSchema.nullable(),
    observedAt: z.string(),
});
/**
 * How long a usage reading counts as the person's usage now (PKG-PM-C Mac
 * walk, 2026-10-23). A reading arrives only while Claude Code runs, and the
 * person also uses Claude elsewhere, so an older reading is never shown as
 * current: the page asks the app to refresh it (`refreshUsage`).
 */
export const USAGE_CURRENT_MS = 5 * 60_000;
/**
 * How far ahead of this computer's clock a reading's `observedAt` may be and
 * still count: the app and the page share the clock, so more than this is a
 * bad timestamp or a clock that stepped back, never a fresh reading.
 */
export const USAGE_CLOCK_TOLERANCE_MS = 60_000;
/**
 * Until when `usage` says the person's usage now (epoch ms, exclusive): read
 * {@link USAGE_CURRENT_MS} ago, or the first window reset after the reading,
 * whichever comes first. A reset at or before `observedAt` had already
 * happened when the reading was taken, so it does not end it. Null for no
 * reading or an `observedAt` that is not a date. The single rule behind
 * {@link isUsageCurrent}; a page uses it to re-render on time.
 */
export function usageCurrentUntil(usage) {
    if (usage === null || usage === undefined)
        return null;
    const observed = Date.parse(usage.observedAt);
    if (!Number.isFinite(observed))
        return null;
    let until = observed + USAGE_CURRENT_MS;
    for (const window of [usage.fiveHour, usage.sevenDay]) {
        if (window?.resetsAt === null || window?.resetsAt === undefined)
            continue;
        const reset = Date.parse(window.resetsAt);
        if (Number.isFinite(reset) && reset > observed)
            until = Math.min(until, reset);
    }
    return until;
}
/**
 * Whether `usage` still says the person's usage now: read within
 * {@link USAGE_CURRENT_MS}, no window has reset since it was read, and not
 * read in the future (beyond {@link USAGE_CLOCK_TOLERANCE_MS}). False for no
 * reading.
 */
export function isUsageCurrent(usage, nowMs = Date.now()) {
    const until = usageCurrentUntil(usage);
    if (until === null || nowMs >= until)
        return false;
    return Date.parse(usage?.observedAt ?? "") <= nowMs + USAGE_CLOCK_TOLERANCE_MS;
}
/** Where the app's refresh of the usage reading stands: `checking` while it asks Claude Code, `failed` when that gave no reading. */
export const CLAUDE_USAGE_CHECK_STATUSES = ["checking", "failed"];
/** The app's last usage refresh: its status and when it started or failed (ISO 8601). */
export const claudeUsageCheckSchema = z.object({
    status: z.enum(CLAUDE_USAGE_CHECK_STATUSES),
    at: z.string(),
});
// ---------------------------------------------------------------------------
// Entries and state (reported by the app; lenient)
// ---------------------------------------------------------------------------
/**
 * One queue entry. Times are ISO 8601. `batchId`, `batchItemId` and
 * `batchMode` link a `groom-item` entry to its SpecTree batch (null until the
 * app has one). `followerOf` names the entry this one waits on (for example
 * a second item of the same project waiting for its repo copies to refresh).
 * `briefId` is the brief SpecTree confirmed for a `briefed` entry.
 *
 * Optional, feature-detected fields (epic PI-SPEC-SW-E290, PKG-PM-B; no
 * contract number moves, older apps leave them out):
 * - `nextInLine`: a single item that jumped ahead of waiting session items
 *   ("Next in line · starts when a run finishes", UX-PM-04);
 * - `wentAheadOf`: how many waiting entries it went ahead of;
 * - `requeuedAt`: when a stopped run went back to the front of the queue
 *   (sleep, Sign Out or quit), ISO 8601;
 * - `detail`: technical detail for "Copy details for the Admin"; never shown
 *   as the entry's sentence.
 */
export const groomingQueueEntrySchema = z
    .object({
    id: z.guid(),
    key: z.string(),
    kind: z.enum(GROOMING_QUEUE_ENTRY_KINDS),
    project: z.string(),
    itemId: z.string().nullable(),
    title: z.string().nullable(),
    batchId: z.string().nullable(),
    batchItemId: z.string().nullable(),
    batchMode: z.enum(GROOMING_BATCH_MODES).nullable(),
    state: z.enum(GROOMING_QUEUE_ENTRY_STATES),
    phase: z.enum(GROOMING_RUN_PHASES).optional(),
    /** One plain phrase, for example "Reading the item". Never a raw tool name, path or prompt. */
    activity: z.string().max(GROOMING_ACTIVITY_MAX_CHARS).optional(),
    followerOf: z.string().optional(),
    queuedAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    errorCode: z.enum(GROOMING_ENGINE_ERROR_CODES).optional(),
    message: z.string().max(GROOMING_QUEUE_MESSAGE_MAX_CHARS).optional(),
    briefId: z.string().nullable().optional(),
    nextInLine: z.boolean().optional(),
    wentAheadOf: z.number().int().min(0).optional(),
    requeuedAt: z.string().optional(),
    detail: z.string().max(GROOMING_QUEUE_MESSAGE_MAX_CHARS).optional(),
})
    .loose();
/**
 * Why the queue is paused. `errorCode` and `fix` (one plain sentence, see
 * {@link GROOMING_QUEUE_SETUP_FIXES}) accompany a `setup` pause; `resumeAt`
 * (ISO 8601) a `usage-limit` pause, when the queue resumes on its own.
 *
 * Pages choose the words by `reason` and `errorCode` from their own contract
 * version (`queuePauseBanner` in `queue-copy.ts`), never from `fix`: an older
 * app keeps sending its own texts there.
 *
 * Optional, feature-detected fields (epic PI-SPEC-SW-E290, PKG-PM-B):
 * `since` (when the pause began, ISO 8601), `detail` (technical detail for
 * "Copy details for the Admin") and `stoppedEntries` (how many running
 * entries the pause stopped and put back at the front). Sign Out reuses the
 * `launch` reason: a new reason would make older pages fail to parse
 * `queue-changed` events.
 */
export const groomingQueuePauseSchema = z
    .object({
    reason: z.enum(GROOMING_QUEUE_PAUSE_REASONS),
    errorCode: z.enum(GROOMING_ENGINE_ERROR_CODES).optional(),
    fix: z.string().max(GROOMING_QUEUE_FIX_MAX_CHARS).optional(),
    resumeAt: z.string().optional(),
    since: z.string().optional(),
    detail: z.string().max(GROOMING_QUEUE_MESSAGE_MAX_CHARS).optional(),
    stoppedEntries: z.number().int().min(0).optional(),
})
    .loose();
/** A concurrency setting inside {@link GROOMING_QUEUE_CONCURRENCY}. */
export const groomingQueueConcurrencySchema = z
    .number()
    .int()
    .min(GROOMING_QUEUE_CONCURRENCY.min)
    .max(GROOMING_QUEUE_CONCURRENCY.max);
/**
 * How much of the 5-hour window one item and the whole waiting queue are
 * expected to use (fractions from 0 upward), from the last `basedOnRuns`
 * runs. Null fractions mean too few runs to estimate.
 */
export const groomingQueueEstimateSchema = z
    .object({
    perItemFiveHour: z.number().min(0).nullable(),
    queueFiveHour: z.number().min(0).nullable(),
    basedOnRuns: z.number().int().min(0),
})
    .loose();
/** Briefs done of all entries in this run of the queue ("3 of 8 briefs are done"). */
export const groomingQueueProgressSchema = z
    .object({
    done: z.number().int().min(0),
    total: z.number().int().min(0),
})
    .loose();
/** The last time the queue started again by itself. */
export const groomingQueuePickedUpSchema = z
    .object({
    after: z.enum(GROOMING_QUEUE_PICKED_UP_AFTER),
    at: z.string(),
    sleptAt: z.string().optional(),
})
    .loose();
/**
 * The whole queue, as `grooming.getQueue()` returns it and `queue-changed`
 * events carry it. `running` and `upNext` are in run order; `completed` holds
 * at most {@link GROOMING_QUEUE_COMPLETED_MAX} finished entries, newest first.
 *
 * Optional, feature-detected fields (epic PI-SPEC-SW-E290, PKG-PM-B):
 * - `keepingAwake`: the app holds the computer awake (the display may still
 *   sleep) because runs are in flight or will start again by themselves;
 * - `progress`: briefs done of all entries in this run of the queue;
 * - `pickedUp`: the queue last started again by itself, after what
 *   ({@link GROOMING_QUEUE_PICKED_UP_AFTER}), when (`at`) and, after a wake,
 *   when the computer slept (`sleptAt`), all ISO 8601.
 * - `usageCheck` (PKG-PM-C Mac walk, 2026-10-23): the app's refresh of the
 *   usage reading (`refreshUsage`), null or absent when none is under way or
 *   the last one succeeded.
 */
export const groomingQueueStateSchema = z
    .object({
    status: z.enum(GROOMING_QUEUE_STATUSES),
    pause: groomingQueuePauseSchema.nullable(),
    concurrency: groomingQueueConcurrencySchema,
    running: z.array(groomingQueueEntrySchema),
    upNext: z.array(groomingQueueEntrySchema),
    completed: z.array(groomingQueueEntrySchema).max(GROOMING_QUEUE_COMPLETED_MAX),
    usage: claudeUsageSnapshotSchema.nullable(),
    estimate: groomingQueueEstimateSchema.nullable(),
    updatedAt: z.string(),
    keepingAwake: z.boolean().optional(),
    progress: groomingQueueProgressSchema.optional(),
    pickedUp: groomingQueuePickedUpSchema.optional(),
    // An unknown check status from a newer app drops the check, never the whole state.
    usageCheck: claudeUsageCheckSchema.nullable().optional().catch(null),
})
    .loose();
/**
 * The outcome of a queue bridge action. On success, `entryId` names the
 * entry added or acted on, `alreadyQueued` says the key was already queued or
 * running (nothing was added; `entryId` is the existing entry) and `batchId`
 * names the SpecTree batch an enqueue created or used.
 */
export const groomingQueueActionResultSchema = z.discriminatedUnion("ok", [
    z
        .object({
        ok: z.literal(true),
        entryId: z.string().optional(),
        alreadyQueued: z.boolean().optional(),
        batchId: z.string().optional(),
    })
        .loose(),
    z
        .object({
        ok: z.literal(false),
        errorCode: z.enum(GROOMING_ENGINE_ERROR_CODES),
        message: z.string(),
    })
        .loose(),
]);
// ---------------------------------------------------------------------------
// Setup fixes
// ---------------------------------------------------------------------------
/**
 * The plain sentence a paused queue shows for each setup failure: one that
 * every remaining entry would hit the same way, so the queue pauses (entries
 * stay queued) instead of failing them. Every engine code whose effect is
 * `batch` and that is not a usage limit has one.
 *
 * Since epic PI-SPEC-SW-E290 (PKG-PM-B) each is the frame B3 banner from the
 * wording sheet (`GROOMING_QUEUE_SETUP_COPY[code].banner` in
 * `queue-copy.ts`): what happened, in plain words, with no command, no page
 * name and no "Resume" (a successful Fix it starts the queue again by
 * itself). What Fix it does and the notification text live on the sheet.
 */
export const GROOMING_QUEUE_SETUP_FIXES = Object.fromEntries(GROOMING_QUEUE_SETUP_CODES.map((code) => [code, GROOMING_QUEUE_SETUP_COPY[code].banner]));
//# sourceMappingURL=queue.js.map